import { endOfMonth, startOfMonth, subMonths } from 'date-fns';

import { getGregorianCalendarLocale } from '@heidi/utils/dates';

import { PRESETS, type PresetName } from './presets';
import { type DateRange } from './types';

export const formatDate = (date?: Date, locale = 'en-US'): string =>
  date
    ? date.toLocaleDateString(getGregorianCalendarLocale(locale), {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '';

/**
 * Normalizes a date to start of day in local timezone (00:00:00.000 local)
 * This ensures filter compares against local dates as users expect
 * @param date - The date to normalize
 * @returns New date set to start of day in local timezone
 */
export const normalizeToStartOfDay = (date: Date): Date => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

/**
 * Normalizes a date to end of day in local timezone (23:59:59.999 local)
 * This ensures filter compares against local dates as users expect
 * @param date - The date to normalize
 * @returns New date set to end of day in local timezone
 */
export const normalizeToEndOfDay = (date: Date): Date => {
  const normalized = new Date(date);
  normalized.setHours(23, 59, 59, 999);
  return normalized;
};

export const getPresetRange = (name: string) => {
  const from = new Date();
  const to = new Date();
  from.setHours(0, 0, 0, 0);
  to.setHours(23, 59, 59, 999);
  switch (name) {
    case 'today':
      break;
    case 'yesterday':
      from.setDate(from.getDate() - 1);
      to.setDate(to.getDate() - 1);
      break;
    case 'last7':
      from.setDate(from.getDate() - 6);
      break;
    case 'last14':
      from.setDate(from.getDate() - 13);
      break;
    case 'last30':
      from.setDate(from.getDate() - 29);
      break;
    case 'last90':
      from.setDate(from.getDate() - 89);
      break;
    case 'thisWeek':
      from.setDate(from.getDate() - from.getDay());
      break;
    case 'lastWeek':
      from.setDate(from.getDate() - from.getDay() - 7);
      to.setDate(to.getDate() - to.getDay() - 1);
      break;
    case 'thisMonth':
      from.setDate(1);
      break;
    // `subMonths` clamps to the target month's last day, so the 31st never rolls
    // into the next month the way `setMonth` leaves it.
    case 'lastMonth': {
      const previousMonth = subMonths(from, 1);
      from.setTime(startOfMonth(previousMonth).getTime());
      to.setTime(endOfMonth(previousMonth).getTime());
      break;
    }
    case 'last12months':
      from.setTime(subMonths(from, 11).getTime());
      break;
  }
  return { from, to };
};

/**
 * Detects which preset was used for a given date range
 * @param range - The date range to check
 * @returns The preset name if matched, 'custom' if no match, or null if range is invalid
 */
export const detectPresetUsed = (range: DateRange): PresetName | null => {
  if (!range.from || !range.to) return null;

  const normalize = (d: Date) => new Date(d).setHours(0, 0, 0, 0);
  const normalizeEnd = (d: Date) => new Date(d).setHours(23, 59, 59, 999);

  for (const preset of PRESETS) {
    if (preset.name === 'custom') continue;
    const { from: pf, to: pt } = getPresetRange(preset.name);
    if (
      normalize(range.from) === normalize(pf) &&
      normalizeEnd(range.to) === normalizeEnd(pt)
    ) {
      return preset.name;
    }
  }

  return 'custom';
};
